<script>
import { useAutomationLocales } from 'dashboard/routes/dashboard/settings/wgptAutomations/composables/useAutomationLocales';
import ButtonNext from 'next/button/Button.vue';
import { mapGetters } from 'vuex';
import dynamicField from '../dynamicField/Index.vue';
import OutputMappingContent from './OutputMappingContent.vue';

export default {
  components: {
    DynamicField: dynamicField,
    OutputMappingContent,
    ButtonNext,
  },
  props: {
    step: {
      type: Object,
      default: () => {},
    },
    variables: {
      type: Array,
      default: () => [],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    dirty: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['stepChange'],
  setup() {
    const { toLocale } = useAutomationLocales();

    return {
      toLocale,
    };
  },
  data() {
    return {
      defaultName: this.$t(
        'WGPT_AUTOMATIONS.EDITOR.ACTION.FALLBACK_ACTION_NAME'
      ),
      updatedName: this.step.name,
      isEditing: false,
    };
  },
  computed: {
    ...mapGetters({
      getParentSteps: 'wgptAutomationBuilder/getParentSteps',
    }),
    availableVariables() {
      return this.variables.filter(variable => {
        if (variable.source) return true;
        if (variable.wrapper) return true;
        if (!variable.model) return true;
        return false;
      });
    },
    stepName() {
      return this.step.name || this.defaultName;
    },
    actionRegistry() {
      return (
        this.$store.getters['wgptAutomationRegistry/getAction'](
          this.step.action
        ) || {}
      );
    },
    latestVersion() {
      return (
        this.$store.getters['wgptAutomationRegistry/getLatestVersion'](
          this.step.action
        ) || {}
      );
    },
    fields() {
      const fields = this.actionRegistry.form_fields || [];
      return fields.map((field, index) => {
        return {
          ...field,
          key: `${this.step.id}_${index}`,
        };
      });
    },
    attributes() {
      return this.step.attributes || {};
    },
    outputMapping() {
      return this.step.output_mapping || {};
    },
    outputMappingFields() {
      return this.actionRegistry.output_mapping_fields || [];
    },
    versionDisplay() {
      const actionName = this.step.action;
      if (!actionName) return '';
      const lastPart = actionName.split('_').pop();
      const hasVersion = /^v\d+$/.test(lastPart);
      return hasVersion ? lastPart : 'v1';
    },
    showUpdateButton() {
      const versionPart = this.versionDisplay;
      const currentVersion = Number(versionPart.slice(1));
      const latestVersion = this.latestVersion.version;
      return currentVersion < latestVersion;
    },
    parentSteps() {
      return this.getParentSteps(this.step.id);
    },
  },
  watch: {
    step(step, stepBefore) {
      if (step.id !== stepBefore.id || step.name !== this.updatedName) {
        this.updatedName = step.name;
      }
    },
  },
  methods: {
    isVisible(field) {
      const conditions = field.show_if ?? [];
      const results = conditions.map(({ field: fieldName, value }) => {
        const currentValue =
          this.attributes[fieldName] ?? this.getDefaultFieldValue(fieldName);
        if (currentValue == null) return false;
        const values = Array.isArray(value) ? value : [value];
        return values.some(v => {
          if (v.includes('*'))
            return `${currentValue}`.includes(v.replace(/\*/g, ''));
          return currentValue === v;
        });
      });
      return results.every(result => result);
    },
    getDefaultFieldValue(fieldName) {
      const field = this.fields.find(({ name }) => name === fieldName);
      return field?.default;
    },
    onFieldChange(key, value) {
      const attributesBefore = this.step?.attributes ?? {};
      const attributes = {
        ...attributesBefore,
        [key]: value,
      };
      this.fields.forEach(field => {
        if (!field.show_if?.length || field.name === key) return;
        if (!this.isVisibleWith(field, attributes)) {
          delete attributes[field.name];
        }
      });
      this.$emit('stepChange', this.step.id, { attributes });
    },
    isVisibleWith(field, attributes) {
      const conditions = field.show_if ?? [];
      if (!conditions.length) return true;
      return conditions.every(({ field: fieldName, value }) => {
        const actual =
          attributes[fieldName] ?? this.getDefaultFieldValue(fieldName);
        if (actual == null) return false;
        const values = Array.isArray(value) ? value : [value];
        return values.some(v => {
          if (v.includes('*')) {
            if (v === '*') return !!actual;
            return `${actual}`.includes(v.replace(/\*/g, ''));
          }
          return actual === v;
        });
      });
    },
    onOutputMappingFieldChange(outputMapping) {
      const stepUpdates = {
        output_mapping: outputMapping,
      };
      this.$emit('stepChange', this.step.id, stepUpdates);
    },
    editName() {
      this.isEditing = true;
      this.$nextTick(() => {
        this.$refs.nameInput.focus();
      });
    },
    onNameChange() {
      const stepUpdates = {
        name: this.updatedName,
      };
      this.$emit('stepChange', this.step.id, stepUpdates);
      this.isEditing = false;
      this.$refs.nameInput.blur();
    },
    cancelNameEditing() {
      this.isEditing = false;
    },
    updateAction() {
      const stepUpdates = {
        action: this.latestVersion.action,
      };
      this.$emit('stepChange', this.step.id, stepUpdates);
    },
  },
};
</script>

<template>
  <div class="p-6 flex flex-col gap-6">
    <header class="group pe-10 flex flex-col gap-1">
      <div class="flex items-center gap-2">
        <input
          v-show="isEditing"
          ref="nameInput"
          v-model.trim="updatedName"
          :placeholder="defaultName"
          class="name-input ignore-drag !ps-1 !bg-n-alpha-black2 disabled:!cursor-text !text-slate-900 dark:!text-slate-100 !border-none !outline !outline-2 !outline-n-blue-border -outline-offset-2 !m-0 text-xl !font-semibold !h-6"
          dir="auto"
          type="text"
          @blur="onNameChange"
          @keyup.enter="onNameChange"
          @keyup.esc="cancelNameEditing"
        />
        <h5
          v-if="!isEditing"
          class="grow text-base font-semibold text-black-900 dark:text-slate-200 select-none inline-flex items-center gap-1.5 overflow-hidden"
        >
          <span class="truncate">{{ stepName }}</span>
          <ButtonNext
            color="slate"
            size="xs"
            variant="ghost"
            icon="i-lucide-pencil"
            class="invisible group-hover:visible ignore-drag"
            @click="editName"
          />
        </h5>
      </div>
      <p v-if="actionRegistry.name" class="mb-0 text-xs text-n-slate-11">
        <span>{{ toLocale(actionRegistry.name) }}</span>
        <span class="ps-1 before:content-['('] after:content-[')']">
          <span>{{ versionDisplay }}</span>
          <template v-if="showUpdateButton">
            <span>&nbsp;-&nbsp;</span>
            <ButtonNext size="xs" variant="link" @click="updateAction">
              {{
                $t('WGPT_AUTOMATIONS.EDITOR.SIDEBAR.ACTIONS.UPDATE_BUTTON_TXT')
              }}
            </ButtonNext>
          </template>
        </span>
      </p>
      <p class="mb-0 text-xs text-n-slate-11">
        <span v-if="actionRegistry.description">
          {{ toLocale(actionRegistry.description) }}
        </span>
        <span v-else class="space-y-2 animate-loader-pulse">
          <div class="bg-slate-100 dark:bg-slate-800/50 rounded w-1/2">
            &nbsp;
          </div>
        </span>
      </p>
    </header>

    <template v-if="fields.length">
      <div class="border-t border-n-weak" />

      <div class="form-fields flex flex-col gap-6">
        <DynamicField
          v-for="field of fields"
          :key="field.key"
          :field="field"
          :value="attributes[field.name]"
          :visible="isVisible(field)"
          :variables="availableVariables"
          :disabled="disabled"
          :dirty="dirty"
          :parent-steps="parentSteps"
          @change="value => onFieldChange(field.name, value)"
        />
      </div>
    </template>

    <template v-if="outputMappingFields.length">
      <div class="border-t border-n-weak" />

      <OutputMappingContent
        :output-mapping="outputMapping"
        :output-mapping-fields="outputMappingFields"
        :variables="availableVariables"
        :disabled="disabled"
        :dirty="dirty"
        @change="onOutputMappingFieldChange"
      />
    </template>
  </div>
</template>
